/**
 * Agent Suggestions Service - Client-side Firestore integration for proposal documents
 *
 * Manages the `agentSuggestions` subcollection written by the travelAgent Cloud Function.
 * Follows the exact same pattern as travelAgentService.js.
 *
 * Firestore Structure:
 * users/{userId}/trips/{tripId}/agentSuggestions/{suggestionId}
 *   - refId, seqNumber, status, action, actionedByUserId, modifiedData, approvedEntityId
 *
 * effectiveUserId Pattern:
 * - All Firestore reads/writes use effectiveUserId (trip owner for shared trips)
 * - See CLAUDE.md "Shared Trip Data Access Pattern"
 */

import { db } from '@trip-planner-monorepo/shared';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';

class AgentSuggestionsService {
  constructor() {
    this._initialized = false;
  }

  /**
   * Initialize the service (deferred init pattern)
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;
    console.log('[AgentSuggestionsService] initialized');
  }

  // ---------------------------------------------------------------------------
  // Real-time Subscriptions
  // ---------------------------------------------------------------------------

  /**
   * Subscribe to agentSuggestions for a trip (real-time).
   *
   * @param {string} effectiveUserId - Trip owner's userId (use effectiveUserId, not currentUser.uid)
   * @param {string} tripId - Trip identifier
   * @param {Object} options
   * @param {string|null} options.statusFilter - Optional status filter ('outstanding'|'approved'|'dismissed'|'modified')
   * @param {Function} options.onUpdate - Called with array of suggestion docs on change
   * @param {Function} [options.onError] - Called with error on subscription failure
   * @returns {Function} Unsubscribe function
   */
  subscribeToSuggestions(effectiveUserId, tripId, { statusFilter = null, onUpdate, onError }) {
    if (!effectiveUserId || !tripId) {
      console.warn('[AgentSuggestionsService] subscribeToSuggestions: missing effectiveUserId or tripId');
      return () => {};
    }

    if (typeof onUpdate !== 'function') {
      console.warn('[AgentSuggestionsService] subscribeToSuggestions: onUpdate must be a function');
      return () => {};
    }

    const suggestionsRef = collection(
      db,
      'users', effectiveUserId,
      'trips', tripId,
      'agentSuggestions'
    );

    let q;
    if (statusFilter) {
      q = query(
        suggestionsRef,
        where('status', '==', statusFilter),
        orderBy('seqNumber', 'asc')
      );
    } else {
      q = query(suggestionsRef, orderBy('seqNumber', 'asc'));
    }

    return onSnapshot(
      q,
      (snapshot) => {
        const suggestions = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || null,
          actionedAt: docSnap.data().actionedAt?.toDate?.()?.toISOString() || null
        }));
        onUpdate(suggestions);
      },
      (error) => {
        console.error('[AgentSuggestionsService] Suggestions subscription error:', error);
        if (typeof onError === 'function') {
          onError(error);
        } else {
          onUpdate([]);
        }
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Individual Document Access
  // ---------------------------------------------------------------------------

  /**
   * Get a single suggestion by its refId (e.g. "A12").
   * Performs a full collection scan since refId is not the document ID.
   *
   * @param {string} effectiveUserId - Trip owner's userId
   * @param {string} tripId - Trip identifier
   * @param {string} refId - The agent-assigned reference ID (e.g. "A12")
   * @returns {Promise<Object|null>} Suggestion document data with id, or null
   */
  async getSuggestionByRef(effectiveUserId, tripId, refId) {
    try {
      if (!effectiveUserId || !tripId || !refId) {
        console.warn('[AgentSuggestionsService] getSuggestionByRef: missing required params');
        return null;
      }

      const suggestionsRef = collection(
        db,
        'users', effectiveUserId,
        'trips', tripId,
        'agentSuggestions'
      );

      const q = query(suggestionsRef, where('refId', '==', refId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.warn(`[AgentSuggestionsService] No suggestion found with refId: ${refId}`);
        return null;
      }

      const docSnap = snapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || null,
        actionedAt: docSnap.data().actionedAt?.toDate?.()?.toISOString() || null
      };
    } catch (error) {
      console.error('[AgentSuggestionsService] getSuggestionByRef error:', error);
      return null;
    }
  }

  /**
   * Get a suggestion by its Firestore document ID.
   *
   * @param {string} effectiveUserId - Trip owner's userId
   * @param {string} tripId - Trip identifier
   * @param {string} suggestionId - Firestore document ID
   * @returns {Promise<Object|null>}
   */
  async getSuggestionById(effectiveUserId, tripId, suggestionId) {
    try {
      if (!effectiveUserId || !tripId || !suggestionId) return null;

      const docRef = doc(
        db,
        'users', effectiveUserId,
        'trips', tripId,
        'agentSuggestions', suggestionId
      );

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;

      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || null,
        actionedAt: docSnap.data().actionedAt?.toDate?.()?.toISOString() || null
      };
    } catch (error) {
      console.error('[AgentSuggestionsService] getSuggestionById error:', error);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Status Updates
  // ---------------------------------------------------------------------------

  /**
   * Update a suggestion's status and related fields.
   *
   * @param {string} effectiveUserId - Trip owner's userId
   * @param {string} tripId - Trip identifier
   * @param {string} suggestionId - Firestore document ID
   * @param {Object} updates
   * @param {string} updates.status - New status: 'approved'|'dismissed'|'modified'|'outstanding'
   * @param {string} [updates.actionedByUserId] - UID of the user who actioned this
   * @param {Object} [updates.modifiedData] - User-provided modifications
   * @param {string} [updates.approvedEntityId] - ID of the entity created on approval
   * @returns {Promise<void>}
   */
  async updateSuggestionStatus(effectiveUserId, tripId, suggestionId, {
    status,
    actionedByUserId = null,
    modifiedData = null,
    approvedEntityId = null
  }) {
    try {
      if (!effectiveUserId || !tripId || !suggestionId || !status) {
        throw new Error('effectiveUserId, tripId, suggestionId, and status are required');
      }

      const docRef = doc(
        db,
        'users', effectiveUserId,
        'trips', tripId,
        'agentSuggestions', suggestionId
      );

      const updatePayload = {
        status,
        actionedAt: serverTimestamp()
      };

      if (actionedByUserId) updatePayload.actionedByUserId = actionedByUserId;
      if (modifiedData) updatePayload.modifiedData = modifiedData;
      if (approvedEntityId) updatePayload.approvedEntityId = approvedEntityId;

      await updateDoc(docRef, updatePayload);
      console.log(`[AgentSuggestionsService] Suggestion ${suggestionId} updated to status: ${status}`);
    } catch (error) {
      console.error('[AgentSuggestionsService] updateSuggestionStatus error:', error);
      throw error;
    }
  }
}

export const agentSuggestionsService = new AgentSuggestionsService();
export default agentSuggestionsService;
